import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

export default function AdminSettings() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || (user?.permissions || []).includes("manage_settings");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  usePoll(() => api.get("/admin/settings").then((r) => setS((prev) => prev || r.data)).catch(() => {}), 60000, []);

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try { const { data } = await api.post("/admin/email/test", { to: testTo }); setTestResult({ ok: true, msg: `Sent (status: ${data.status})` }); }
    catch (e) { setTestResult({ ok: false, msg: errMsg(e) }); } finally { setTesting(false); }
  };

  if (!s) return <div className="text-slate-400">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        telegram_url: s.telegram_url, global_rate: s.global_rate, maintenance_mode: s.maintenance_mode,
        pause_deposits: s.pause_deposits, pause_orders: s.pause_orders, maintenance_message: s.maintenance_message,
        trc20_address: s.trc20_address, bep20_address: s.bep20_address,
        trc20_active: s.trc20_active, bep20_active: s.bep20_active,
        require_qr: s.require_qr, email_from_name: s.email_from_name, email_reply_to: s.email_reply_to,
        admin_notification_email: s.admin_notification_email,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const Field = ({ k, label, placeholder }) => (
    <label className="block"><span className="text-sm font-medium text-slate-700">{label}</span>
      <input disabled={!canManage} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} placeholder={placeholder}
        data-testid={`settings-${k}`} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm disabled:bg-slate-50" /></label>
  );
  const Toggle = ({ k, label }) => (
    <label className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input type="checkbox" disabled={!canManage} checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} data-testid={`settings-toggle-${k}`} className="w-5 h-5" />
    </label>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Settings</h1>
      {!canManage && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">You can view settings but not edit them.</div>}

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-heading font-semibold text-slate-900">General</h2>
        <Field k="telegram_url" label="Telegram support URL" placeholder="https://t.me/…" />
        <Field k="global_rate" label="Global default sell rate (INR/USDT)" />
        <Field k="email_from_name" label="Email sender name" placeholder="TrustFlow" />
        <Field k="email_reply_to" label="Reply-to / contact email" placeholder="support@yourdomain" />
        <Field k="admin_notification_email" label="Admin notification email (new order alerts)" placeholder="admin@yourdomain" />
        <Toggle k="require_qr" label="Require UPI QR upload on every sell order" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
        <h2 className="font-heading font-semibold text-slate-900">Email test</h2>
        <div className="flex gap-2">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@email.com" data-testid="email-test-to"
            className="flex-1 px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
          <button onClick={sendTest} disabled={testing || !canManage} data-testid="send-test-email-button"
            className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">{testing ? "Sending…" : "Send test"}</button>
        </div>
        {testResult && <div className={`text-sm ${testResult.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="email-test-result">{testResult.msg}</div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-heading font-semibold text-slate-900">Deposit addresses</h2>
        <Field k="trc20_address" label="TRC20 receiving address" />
        <Toggle k="trc20_active" label="TRC20 network active (accept deposits)" />
        <Field k="bep20_address" label="BEP20 receiving address" />
        <Toggle k="bep20_active" label="BEP20 network active (accept deposits)" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
        <h2 className="font-heading font-semibold text-slate-900">Maintenance</h2>
        <Toggle k="maintenance_mode" label="Maintenance mode (pause deposits & orders)" />
        <Toggle k="pause_deposits" label="Pause new deposits" />
        <Toggle k="pause_orders" label="Pause new sell orders" />
        <Field k="maintenance_message" label="Maintenance message" />
      </div>

      {canManage && (
        <button onClick={save} disabled={busy} data-testid="settings-save-button" className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">Save settings</button>
      )}
    </div>
  );
}
